import {
  FieldInputted,
  ElementClicked,
  ProductClicked,
  ProductListViewed,
  ProductViewed
} from '@/types/tracking'
import { Agent } from '@/types/agents'
import { helmetJsonLdProp } from 'react-schemaorg'
import { ItemList } from 'schema-dts'

/**
 * Recursively traverses an object and lowercases all of its values
 * @param payload The object that needs its values lowercased
 * @param keysToSkip A collection of keys where the logic doesn't apply
 */
const lowerCaseValues = (payload: unknown, keysToSkip: string[] = []) => {
  if (!payload) {
    return
  }
  return Object.entries(payload).reduce((updated, [key, value]) => {
    if (keysToSkip.includes(key)) {
      updated[key] = value
      return updated
    }

    const isString = typeof value === 'string'
    const isObject = typeof value === 'object'
    const isArray = Array.isArray(value)

    if (isArray) {
      updated[key] = value.map(val => lowerCaseValues(val, keysToSkip))
      return updated
    }

    if (isObject) {
      updated[key] = lowerCaseValues(value, keysToSkip)
      return updated
    }

    updated[key] = isString ? value.toLowerCase() : value

    return updated
  }, {})
}

/**
 * @desc Removes all enumerated properties from target object
 */
const omit = (target: Record<string, unknown> = {}, props: string[]) => {
  target = { ...target }
  props.forEach(prop => delete target[prop])
  return target
}

const toQueryParams = (payload: Record<string, unknown>): string => {
  return (
    Object.entries(payload)
      /**
       * Remove anything that is null or undefined
       */
      .filter(([, value]) => value !== null && value !== undefined)
      /**
       * Convert the object to a query params string
       */
      .map(([key, value]) => `${key}=${value}`)
      .join('&')
  )
}

const isObject = (value: unknown): boolean => {
  return !!value && Object.prototype.toString.call(value) === '[object Object]'
}

const isNil = (value: unknown): boolean => value === null || value === undefined

/**
 * @desc Removes all properties from target object that have undefined or null values
 */
const cleanNilValues = (target: unknown) => {
  return Object.entries(target).reduce((result, [key, value]) => {
    if (!isNil(value)) {
      result[key] = value
    }

    return result
  }, {})
}

const flattenObject = (obj: unknown) => {
  return Object.entries(obj).reduce((flattened, [key, value]) => {
    if (isObject(value) && value !== null && !Array.isArray(value)) {
      return Object.assign(flattened, flattenObject(value))
    }

    return { ...flattened, [key]: value }
  }, {})
}

const pluralize = (count: number, word: string, suffix = 's') =>
  `${count} ${word}${count !== 1 ? suffix : ''}`

const buildElementClickedPayload = (
  elementType: string,
  location: string,
  position: string,
  name: string,
  text: string,
  actionOutcome: string,
  outboundUrl?: string
) => {
  return {
    actionOutcome,
    outboundUrl: outboundUrl || '',
    webElement: {
      elementType,
      location,
      position,
      name,
      text,
      htmlId: ''
    }
  } as ElementClicked
}

const buildProductClickedPayload = (
  listId: string,
  brand: string,
  category: string,
  productId: string,
  location: string,
  position: number
) => {
  return {
    listId,
    product: {
      brand,
      category,
      location,
      position,
      price: 0,
      productId,
      variant: ''
    }
  } as ProductClicked
}

const buildProductViewedPayload = (
  listId: string,
  brand: string,
  category: string,
  productId: string,
  location: string,
  position: number
) => {
  return {
    listId,
    product: {
      brand,
      category,
      location,
      position,
      price: 0,
      productId,
      text: '',
      variant: ''
    }
  } as ProductViewed
}

const buildFieldInputtedPayload = (
  fieldName: string,
  fieldLabel: string,
  fieldValue: string
) => {
  return {
    userInputField: {
      fieldName: fieldName,
      fieldType: fieldName.toUpperCase(),
      fieldLabel: fieldLabel,
      fieldValue: fieldValue
    },
    formContext: {
      formName: 'realestate',
      formType: 'agent_table',
      formId: '',
      formVersion: ''
    }
  } as FieldInputted
}

const buildProductListViewedPayload = (
  queryRespResluts: Agent[],
  viewCount?: number
) => {
  let viewResult = queryRespResluts

  if (viewCount && queryRespResluts.length > viewCount) {
    viewResult = queryRespResluts.slice(0, viewCount)
  }

  const products = viewResult.map((result, index) => ({
    brand: result.full_name || '',
    category: result.details.specialties
      ? result.details.specialties.join(',')
      : '',
    location: 'bam agent results',
    position: index + 1,
    price: 0,
    productId: '',
    text: '',
    variant: ''
  }))

  return {
    listId: '',
    listName: 'bam agent results',
    category: '',
    product: products,
    field: [
      {
        fieldLabel: 'ZIP code',
        fieldName: 'zipcode',
        fieldType: 'ZIPCODE',
        fieldValue: ''
      },
      {
        fieldLabel: 'Works with',
        fieldName: 'workswith',
        fieldType: 'buy|sell|buy-sell',
        fieldValue: ''
      },
      {
        fieldLabel: 'Listing price range',
        fieldName: 'pricerangefrom',
        fieldType: 'number',
        fieldValue: 0
      },
      {
        fieldLabel: 'Listing price range',
        fieldName: 'pricerangeto',
        fieldType: 'number',
        fieldValue: 0
      },
      {
        fieldLabel: 'Listing property type',
        fieldName: 'propertytype',
        fieldType: 'single_family|townhome|condominium',
        fieldValue: ''
      },
      {
        fieldLabel: 'sort_by',
        fieldName: 'sortby',
        fieldType: 'experience|transactions',
        fieldValue: ''
      }
    ],
    paidListings: 0,
    featuredListings: 0,
    reasonNoCoverage: '',
    standardListings: 0,
    editorialListings: 0,
    sponsoredListings: 0
  } as ProductListViewed
}

export default {
  pluralize,
  lowerCaseValues,
  omit,
  toQueryParams,
  isObject,
  isNil,
  cleanNilValues,
  flattenObject,
  buildFieldInputtedPayload,
  buildElementClickedPayload,
  buildProductClickedPayload,
  buildProductViewedPayload,
  buildProductListViewedPayload
}
